import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Dumbbell, Save, CheckCircle2, HeartPulse } from 'lucide-react';
import { intakeAPI } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { FieldError } from '../components/ui/FieldError';
import { inputClass } from '../lib/validation';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-glow">
            <Dumbbell className="w-6 h-6 text-white" />
          </div>
          <span className="font-display text-2xl font-bold text-gradient">RUTINEO</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Intake() {
  const { token } = useParams<{ token: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['intake', token],
    queryFn: () => intakeAPI.get(token!),
    enabled: !!token,
    retry: false,
  });

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    birthDate: '',
    sex: '' as '' | 'male' | 'female' | 'other',
    heightCm: '',
    weightKg: '',
    goal: '',
    otherSports: '',
    healthNotes: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  // Prefill con lo que el entrenador ya cargó.
  useEffect(() => {
    if (data?.student) {
      const s = data.student;
      setForm((f) => ({
        ...f,
        firstName: s.firstName || '',
        lastName: s.lastName || '',
        phone: s.phone || '',
        birthDate: s.birthDate ? String(s.birthDate).split('T')[0] : '',
        sex: s.sex || '',
        heightCm: s.heightCm != null ? String(s.heightCm) : '',
        weightKg: s.weightKg != null ? String(s.weightKg) : '',
        goal: s.goal || '',
        otherSports: s.otherSports || '',
        healthNotes: s.healthNotes || '',
      }));
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () =>
      intakeAPI.submit(token!, {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone || undefined,
        birthDate: form.birthDate || undefined,
        sex: form.sex || undefined,
        heightCm: form.heightCm ? Number(form.heightCm) : undefined,
        weightKg: form.weightKg ? Number(form.weightKg) : undefined,
        goal: form.goal || undefined,
        otherSports: form.otherSports || undefined,
        healthNotes: form.healthNotes || undefined,
      }),
    onSuccess: () => setDone(true),
    onError: (e: Error) => setErrors({ _form: e.message }),
  });

  const set = (name: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!form.firstName.trim()) errs.firstName = 'El nombre es obligatorio';
    if (!form.lastName.trim()) errs.lastName = 'El apellido es obligatorio';
    if (form.heightCm && Number(form.heightCm) <= 0) errs.heightCm = 'Altura inválida';
    if (form.weightKg && Number(form.weightKg) <= 0) errs.weightKg = 'Peso inválido';
    setErrors(errs);
    if (Object.keys(errs).length === 0) mutation.mutate();
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="card flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      </Shell>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <div className="card text-center py-10">
          <h1 className="text-lg font-semibold text-dark-100 mb-2">Link no válido</h1>
          <p className="text-dark-400">
            Este link de ficha no es válido o ya expiró. Pedile a tu entrenador que te
            genere uno nuevo.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="card text-center py-10">
          <CheckCircle2 className="w-14 h-14 mx-auto text-primary-400 mb-4" />
          <h1 className="text-xl font-semibold text-dark-50 mb-2">¡Listo!</h1>
          <p className="text-dark-400">
            Tus datos se enviaron a tu entrenador. Ya podés cerrar esta página.
          </p>
        </div>
      </Shell>
    );
  }

  const trainerName = data.trainer
    ? `${data.trainer.firstName} ${data.trainer.lastName}`.trim()
    : 'Tu entrenador';

  return (
    <Shell>
      <div className="card space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-dark-50">Completá tu ficha</h1>
          <p className="section-subtitle">
            {trainerName} te pidió estos datos para armar tu entrenamiento. Solo te lleva un
            minuto.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre *</label>
              <input className={inputClass(!!errors.firstName)} value={form.firstName} onChange={set('firstName')} />
              <FieldError message={errors.firstName} />
            </div>
            <div>
              <label className="label">Apellido *</label>
              <input className={inputClass(!!errors.lastName)} value={form.lastName} onChange={set('lastName')} />
              <FieldError message={errors.lastName} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Teléfono</label>
              <input type="tel" className="input" value={form.phone} onChange={set('phone')} placeholder="+54 11 1234-5678" />
            </div>
            <div>
              <label className="label">Fecha de nacimiento</label>
              <input type="date" className="input" value={form.birthDate} onChange={set('birthDate')} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Sexo</label>
              <select className="input" value={form.sex} onChange={set('sex')}>
                <option value="">Sin especificar</option>
                <option value="male">Masculino</option>
                <option value="female">Femenino</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div>
              <label className="label">Altura (cm)</label>
              <input type="number" inputMode="numeric" min="0" className={inputClass(!!errors.heightCm)} value={form.heightCm} onChange={set('heightCm')} placeholder="175" />
              <FieldError message={errors.heightCm} />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="label">Peso (kg)</label>
              <input type="number" inputMode="decimal" min="0" step="0.1" className={inputClass(!!errors.weightKg)} value={form.weightKg} onChange={set('weightKg')} placeholder="72.5" />
              <FieldError message={errors.weightKg} />
            </div>
          </div>

          <div>
            <label className="label">Objetivo</label>
            <input className="input" value={form.goal} onChange={set('goal')} placeholder="Hipertrofia, bajar de peso, salud..." />
          </div>

          <div>
            <label className="label">Otros deportes que practicás</label>
            <input className="input" value={form.otherSports} onChange={set('otherSports')} placeholder="Fútbol, running, escalada..." />
          </div>

          <div>
            <label className="label flex items-center gap-1.5">
              <HeartPulse className="w-3.5 h-3.5 text-primary-400" />
              Lesiones / condiciones de salud
            </label>
            <textarea
              className="input resize-none"
              rows={3}
              value={form.healthNotes}
              onChange={set('healthNotes')}
              placeholder="Lesiones actuales o pasadas, condiciones médicas, medicación, lo que tu entrenador deba saber."
            />
            <p className="text-xs text-dark-500 mt-1">Importante para entrenar seguro. Si no tenés nada, dejalo vacío.</p>
          </div>

          {errors._form && <FieldError message={errors._form} />}

          <button type="submit" disabled={mutation.isPending} className="btn-primary w-full">
            {mutation.isPending ? <LoadingSpinner size="sm" /> : (<><Save className="w-4 h-4" />Enviar ficha</>)}
          </button>
        </form>
      </div>
    </Shell>
  );
}
